import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  collection,
  collectionGroup,
  doc,
  getDoc,
  getDocs
} from 'firebase/firestore'
import { db } from '../firebase'
import { BONUS_LIST } from '../components/BonusList'
import { getFactionStoredWrestlers } from '../utils/factionWrestlers'

const RECENT_DAYS = 30

const getDateValue = value => {
  if (!value) return null
  if (typeof value?.toDate === 'function') return value.toDate()
  if (value instanceof Date) return value
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000)
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const getRefId = value => {
  if (!value) return null
  if (typeof value === 'string') return value
  if (typeof value?.id === 'string') return value.id
  if (typeof value?.path === 'string') {
    const parts = value.path.split('/')
    return parts[parts.length - 1] || null
  }
  return null
}

const formatDelta = value => `${value > 0 ? '+' : ''}${Math.round(value)}`

function Statistiche () {
  const navigate = useNavigate()
  const [selectedBonus, setSelectedBonus] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [stats, setStats] = useState({
    wrestlers: [],
    factions: [],
    swaps: [],
    participations: []
  })

  useEffect(() => {
    async function fetchData () {
      setLoading(true)
      setError('')

      try {
        const [wSnap, fSnap, pSnap] = await Promise.all([
          getDocs(collection(db, 'wrestlers')),
          getDocs(collection(db, 'factions')),
          getDocs(collectionGroup(db, 'participations'))
        ])

        const factions = fSnap.docs.map(docSnap => ({
          id: docSnap.id,
          ...docSnap.data()
        }))

        const swapSnapshots = await Promise.all(
          factions.map(faction =>
            getDocs(collection(db, 'factions', faction.id, 'swaps'))
          )
        )

        const wrestlers = wSnap.docs.filter(docSnap => !docSnap.data()?.isGuest).map(docSnap => {
          const data = docSnap.data() || {}
          const prices = Array.isArray(data.prices) ? data.prices : []
          const latestPrice =
            prices.length > 0 ? prices[prices.length - 1]?.value : null

          return {
            id: docSnap.id,
            name: data.name || data.nome || 'Sconosciuto',
            imageUrl: data.imageUrl || '',
            brand: data.brand || '',
            score: data.score || 0,
            updatedAt: getDateValue(data.updatedAt),
            price: data.price ?? latestPrice ?? data.quotation ?? 0
          }
        })

        const swaps = swapSnapshots.flatMap((swapSnap, index) =>
          swapSnap.docs.map(swapDoc => ({
            id: swapDoc.id,
            factionId: factions[index].id,
            ...swapDoc.data()
          }))
        )

        const participations = await Promise.all(
          pSnap.docs.map(async docSnap => {
            const data = docSnap.data() || {}
            const pathParts = docSnap.ref.path.split('/')
            const showId = pathParts[1]
            let showDate = null

            if (showId) {
              try {
                const showSnap = await getDoc(doc(db, 'shows', showId))
                if (showSnap.exists()) {
                  showDate = getDateValue(showSnap.data()?.data)
                }
              } catch (showError) {
                console.error(
                  'Errore nel caricamento show per participation:',
                  showError
                )
              }
            }

            return {
              id: docSnap.id,
              wrestlerId: data.wrestlerId || null,
              totalPoints: data.totalPoints || 0,
              showDate
            }
          })
        )

        setStats({ wrestlers, factions, swaps, participations })
      } catch (loadError) {
        console.error(loadError)
        setError('Errore nel caricamento delle statistiche.')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const computedStats = useMemo(() => {
    const wrestlersById = {}
    stats.wrestlers.forEach(wrestler => {
      wrestlersById[wrestler.id] = wrestler
    })

    const pickCounts = new Map()
    const totalContribution = new Map()
    const averageContributionBuckets = new Map()
    const factionGrowthRows = []
    const recentParticipationBuckets = new Map()

    const recentThreshold = new Date()
    recentThreshold.setDate(recentThreshold.getDate() - RECENT_DAYS)

    stats.participations.forEach(participation => {
      if (
        !participation.wrestlerId ||
        !participation.showDate ||
        participation.showDate < recentThreshold
      ) {
        return
      }

      const bucket = recentParticipationBuckets.get(
        participation.wrestlerId
      ) || { total: 0, count: 0 }
      bucket.total += participation.totalPoints || 0
      bucket.count += 1
      recentParticipationBuckets.set(participation.wrestlerId, bucket)
    })

    stats.factions.forEach(faction => {
      const storedMembers = getFactionStoredWrestlers(faction)
      let recentGrowth = 0

      storedMembers.forEach(member => {
        const wrestler = wrestlersById[member.id] || {}
        const contribution = (wrestler.score || 0) - (member.startScore || 0)

        pickCounts.set(member.id, (pickCounts.get(member.id) || 0) + 1)
        totalContribution.set(
          member.id,
          (totalContribution.get(member.id) || 0) + contribution
        )

        const avgBucket = averageContributionBuckets.get(member.id) || {
          total: 0,
          count: 0
        }
        avgBucket.total += contribution
        avgBucket.count += 1
        averageContributionBuckets.set(member.id, avgBucket)

        if (wrestler.updatedAt && wrestler.updatedAt >= recentThreshold) {
          recentGrowth += contribution
        }
      })

      factionGrowthRows.push({
        id: faction.id,
        name: faction.name || 'Senza nome',
        growth: recentGrowth
      })
    })

    const wrestlerRows = stats.wrestlers.map(wrestler => {
      const averageBucket = averageContributionBuckets.get(wrestler.id) || {
        total: 0,
        count: 0
      }
      const recentParticipationBucket = recentParticipationBuckets.get(
        wrestler.id
      ) || { total: 0, count: 0 }

      return {
        ...wrestler,
        pickCount: pickCounts.get(wrestler.id) || 0,
        totalFactionGain: totalContribution.get(wrestler.id) || 0,
        averageFactionGain: averageBucket.count
          ? averageBucket.total / averageBucket.count
          : 0,
        recentAverageGain: recentParticipationBucket.count
          ? recentParticipationBucket.total / recentParticipationBucket.count
          : null,
        recentEventCount: recentParticipationBucket.count || 0
      }
    })

    const pickedRows = wrestlerRows
      .filter(row => row.pickCount > 0)
      .sort(
        (a, b) =>
          b.pickCount - a.pickCount || a.name.localeCompare(b.name, 'it')
      )

    const leastPickedRows = wrestlerRows
      .slice()
      .sort(
        (a, b) =>
          a.pickCount - b.pickCount || a.name.localeCompare(b.name, 'it')
      )

    const recentPerformanceRows = wrestlerRows
      .filter(row => row.recentAverageGain !== null)
      .sort(
        (a, b) =>
          b.recentAverageGain - a.recentAverageGain ||
          a.name.localeCompare(b.name, 'it')
      )

    const factionGainRows = wrestlerRows
      .filter(row => row.pickCount > 0)
      .sort(
        (a, b) =>
          b.averageFactionGain - a.averageFactionGain ||
          a.name.localeCompare(b.name, 'it')
      )

    const mostUsedCostAverage = pickedRows
      .slice(0, 10)
      .reduce((sum, row) => sum + (row.price || 0), 0)
    const costAverageTopPicked = pickedRows.length
      ? Math.round(mostUsedCostAverage / Math.min(10, pickedRows.length))
      : 0

    const wrestlerInCounts = new Map()
    const wrestlerOutCounts = new Map()

    stats.swaps.forEach(swap => {
      const wrestlerInId = getRefId(swap.wrestlerIn)
      const wrestlerOutId = getRefId(swap.wrestlerOut)

      if (wrestlerInId)
        wrestlerInCounts.set(
          wrestlerInId,
          (wrestlerInCounts.get(wrestlerInId) || 0) + 1
        )
      if (wrestlerOutId)
        wrestlerOutCounts.set(
          wrestlerOutId,
          (wrestlerOutCounts.get(wrestlerOutId) || 0) + 1
        )
    })

    const boughtRows = Array.from(wrestlerInCounts.entries())
      .map(([id, count]) => ({
        ...(wrestlersById[id] || {
          id,
          name: `Wrestler ${id}`,
          imageUrl: '',
          brand: ''
        }),
        count
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'it'))

    const releasedRows = Array.from(wrestlerOutCounts.entries())
      .map(([id, count]) => ({
        ...(wrestlersById[id] || {
          id,
          name: `Wrestler ${id}`,
          imageUrl: '',
          brand: ''
        }),
        count
      }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, 'it'))

    const factionGrowthTop = factionGrowthRows
      .filter(row => row.growth !== 0)
      .sort((a, b) => b.growth - a.growth || a.name.localeCompare(b.name, 'it'))

    return {
      totalFactions: stats.factions.length,
      totalSwaps: stats.swaps.length,
      neverPicked: wrestlerRows.filter(row => row.pickCount === 0).length,
      costAverageTopPicked,
      topPicked: pickedRows.slice(0, 10),
      leastPicked: leastPickedRows.slice(0, 10),
      recentPerformance: recentPerformanceRows.slice(0, 10),
      factionGain: factionGainRows.slice(0, 10),
      mostBought: boughtRows.slice(0, 10),
      mostReleased: releasedRows.slice(0, 10),
      factionGrowth: factionGrowthTop.slice(0, 10)
    }
  }, [stats])

  const renderWrestlerTable = (rows, rightLabel, rightValue, emptyText) => (
    <div className='table-responsive stats-table-wrapper'>
      <table className='table table-hover table-dark align-middle mb-0 stats-table'>
        <thead>
          <tr>
            <th>Wrestler</th>
            <th className='text-end'>{rightLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows.map(row => (
              <tr
                key={row.id}
                className='stats-table-row'
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/wrestlers/${row.id}`)}
              >
                <td>
                  <div className='d-flex align-items-center gap-2'>
                    {row.imageUrl ? (
                      <img
                        src={row.imageUrl}
                        alt={row.name}
                        className='rounded bg-brand-primary'
                        style={{ width: 42, height: 42, objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        className='rounded d-flex align-items-center justify-content-center'
                        style={{
                          width: 42,
                          height: 42,
                          background: 'var(--brand-primary)'
                        }}
                      >
                        <i className='bi bi-person text-white' />
                      </div>
                    )}
                    <div className='min-w-0'>
                      <div className='fw-semibold text-light text-truncate'>
                        {row.name}
                      </div>
                      <small className='text-muted-brand'>
                        {row.brand || 'Brand N/D'}
                      </small>
                    </div>
                  </div>
                </td>
                <td className='text-end fw-bold text-brand-secondary-light'>
                  {rightValue(row)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={2} className='text-center py-4 text-muted-brand'>
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )

  return (
    <div className='stats-page'>
      <div className='container py-4'>
        <div className='d-flex align-items-start justify-content-center gap-2 flex-wrap'>
          <div>
            <h1
              className='mb-3 fw-bold text-center text-brand-secondary'
              style={{
                fontFamily: "'Cinzel Decorative'",
                textShadow: '1px 1px 2px rgba(0, 0, 0)'
              }}
            >
              <i
                className='bi bi-bar-chart-fill me-3'
                style={{
                  fontSize: '3rem',
                  filter: 'drop-shadow(0 2px 4px rgba(194, 157, 84, 0.4))'
                }}
              />
              Statistiche
            </h1>
          </div>
        </div>

        <div className='card border-0 mb-4 stats-card stats-card-gold'>
          <div className='border-bottom px-3 py-3 stats-card-header'>
            <h4 className='h5 mb-0 fw-bold text-brand-secondary-light'>
              <i
                className='bi bi-award-fill me-2'
                style={{ color: 'var(--brand-secondary)', fontSize: '1.2rem' }}
              ></i>
              Bonus/Malus
            </h4>
          </div>

          <div className='card-body stats-card-body'>
            <div className='row g-2 align-items-end'>
              <div className='col-12 col-md-8'>
                <label className='form-label small text-brand-secondary-light fw-semibold'>
                  Seleziona un bonus/malus
                </label>
                <select
                  className='form-select stats-control'
                  value={selectedBonus}
                  onChange={event => setSelectedBonus(event.target.value)}
                >
                  <option value=''>-- Scegli --</option>
                  {BONUS_LIST.slice()
                    .sort((a, b) => a.label.localeCompare(b.label))
                    .map(bonus => (
                      <option key={bonus.value} value={bonus.value}>
                        {bonus.label} ({bonus.points > 0 ? '+' : ''}
                        {bonus.points})
                      </option>
                    ))}
                </select>
              </div>

              <div className='col-12 col-md-4'>
                <button
                  className='w-100 stats-action-btn'
                  disabled={!selectedBonus}
                  onClick={() =>
                    navigate(`/bonus/${encodeURIComponent(selectedBonus)}`)
                  }
                >
                  Apri dettaglio bonus
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className='card border-0 mb-4 stats-card stats-card-primary'>
          <div className='border-bottom px-3 py-3 stats-card-header'>
            <h4 className='h5 mb-0 fw-bold text-brand-secondary-light'>
              <i
                className='bi bi-graph-up-arrow me-2'
                style={{ color: 'var(--brand-secondary)', fontSize: '1.2rem' }}
              ></i>
              Panoramica
            </h4>
          </div>

          <div className='card-body stats-card-body'>
            {loading ? (
              <div className='text-center py-5'>
                <div
                  className='spinner-border text-brand-secondary'
                  role='status'
                />
                <div className='mt-2 text-muted-brand'>Caricamento...</div>
              </div>
            ) : error ? (
              <div className='alert mb-0 stats-error-box'>{error}</div>
            ) : (
              <div className='row g-3'>
                <div className='col-12 col-md-6 col-xl-3'>
                  <div className='p-3 rounded stats-info-box stats-info-box-gold'>
                    <span className='small text-brand-secondary-light fw-semibold'>
                      Fazioni analizzate
                    </span>
                    <span
                      className='ms-auto fw-bold'
                      style={{
                        color: 'var(--brand-secondary)',
                        fontSize: '1.2rem'
                      }}
                    >
                      {computedStats.totalFactions}
                    </span>
                  </div>
                </div>
                <div className='col-12 col-md-6 col-xl-3'>
                  <div className='p-3 rounded stats-info-box stats-info-box-money'>
                    <span className='small text-brand-secondary-light fw-semibold'>
                      Costo medio top scelti
                    </span>
                    <span
                      className='ms-auto fw-bold'
                      style={{ color: 'var(--money)', fontSize: '1.2rem' }}
                    >
                      {computedStats.costAverageTopPicked}
                    </span>
                  </div>
                </div>
                <div className='col-12 col-md-6 col-xl-3'>
                  <div className='p-3 rounded stats-info-box stats-info-box-accent'>
                    <span className='small text-brand-secondary-light fw-semibold'>
                      Wrestler mai scelti
                    </span>
                    <span
                      className='ms-auto fw-bold'
                      style={{
                        color: 'var(--brand-accent)',
                        fontSize: '1.2rem'
                      }}
                    >
                      {computedStats.neverPicked}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {!loading && !error && (
          <>
            <div className='row g-4 mb-4'>
              <div className='col-12 col-xl-6'>
                <div className='card border-0 h-100 stats-card stats-card-primary'>
                  <div className='border-bottom px-3 py-3 stats-card-header'>
                    <h4 className='h5 mb-0 fw-bold text-brand-secondary-light'>
                      Wrestler più scelti
                    </h4>
                  </div>
                  <div className='card-body stats-card-body'>
                    {renderWrestlerTable(
                      computedStats.topPicked,
                      'Fazioni',
                      row => row.pickCount,
                      'Nessun wrestler scelto.'
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className='row g-4 mb-4'>
              <div className='col-12 col-xl-6'>
                <div className='card border-0 h-100 stats-card stats-card-primary'>
                  <div className='border-bottom px-3 py-3 stats-card-header'>
                    <h4 className='h5 mb-0 fw-bold text-brand-secondary-light'>
                      Miglior rendimento medio ultimo mese
                    </h4>
                  </div>
                  <div className='card-body stats-card-body'>
                    {renderWrestlerTable(
                      computedStats.recentPerformance,
                      'Media show',
                      row => formatDelta(row.recentAverageGain || 0),
                      "Nessun wrestler con punteggi show nell'ultimo mese."
                    )}
                  </div>
                </div>
              </div>

              <div className='col-12 col-xl-6'>
                <div className='card border-0 h-100 stats-card stats-card-gold'>
                  <div className='border-bottom px-3 py-3 stats-card-header'>
                    <h4 className='h5 mb-0 fw-bold text-brand-secondary-light'>
                      Wrestler più performanti
                    </h4>
                  </div>
                  <div className='card-body stats-card-body'>
                    {renderWrestlerTable(
                      computedStats.factionGain,
                      'Media/fazione',
                      row => formatDelta(row.averageFactionGain),
                      'Nessun contributo disponibile.'
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className='row g-4 mb-4'>
              <div className='col-12 col-xl-6'>
                <div className='card border-0 h-100 stats-card stats-card-primary'>
                  <div className='border-bottom px-3 py-3 stats-card-header'>
                    <h4 className='h5 mb-0 fw-bold text-brand-secondary-light'>
                      Wrestler più acquistati
                    </h4>
                  </div>
                  <div className='card-body stats-card-body'>
                    {renderWrestlerTable(
                      computedStats.mostBought,
                      'Acquisti',
                      row => row.count,
                      'Nessun acquisto registrato.'
                    )}
                  </div>
                </div>
              </div>

              <div className='col-12 col-xl-6'>
                <div className='card border-0 h-100 stats-card stats-card-accent'>
                  <div className='border-bottom px-3 py-3 stats-card-header'>
                    <h4 className='h5 mb-0 fw-bold text-brand-secondary-light'>
                      Wrestler più svincolati
                    </h4>
                  </div>
                  <div className='card-body stats-card-body'>
                    {renderWrestlerTable(
                      computedStats.mostReleased,
                      'Svincoli',
                      row => row.count,
                      'Nessuno svincolo registrato.'
                    )}
                  </div>
                </div>
              </div>
            </div>
            {/*
            <div className="card border-0 stats-card stats-card-gold">
              <div className="border-bottom px-3 py-3 stats-card-header">
                <h4 className="h5 mb-0 fw-bold text-brand-secondary-light">
                  <i className="bi bi-lightning-charge-fill me-2" style={{ color: "var(--brand-secondary)", fontSize: "1.2rem" }}></i>
                  Fazioni con crescita punti più alta nell&apos;ultimo periodo
                </h4>
              </div>
              <div className="card-body stats-card-body">
                <div className="table-responsive stats-table-wrapper">
                  <table className="table table-hover table-dark align-middle mb-0 stats-table">
                    <thead>
                      <tr>
                        <th>Fazione</th>
                        <th className="text-end">Crescita 30g</th>
                      </tr>
                    </thead>
                    <tbody>
                      {computedStats.factionGrowth.length > 0 ? (
                        computedStats.factionGrowth.map((faction) => (
                          <tr
                            key={faction.id}
                            className="stats-table-row"
                            style={{ cursor: "pointer" }}
                            onClick={() => navigate(`/factions/${faction.id}`)}
                          >
                            <td className="fw-semibold text-light">{faction.name}</td>
                            <td className="text-end fw-bold text-brand-secondary-light">{formatDelta(faction.growth)}</td>
                            </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={2} className="text-center py-4 text-muted-brand">
                            Nessuna crescita recente disponibile.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            */}
          </>
        )}
      </div>
    </div>
  )
}

export default Statistiche
